import { ApiCall } from '../helper/axios';
import { apiEndpoint } from '../helper/commonApi';
import { readSse } from '../helper/sse';
import { store } from '../redux/store';
const BASE = '/api/ai';
const aiService = {
    // Drawer open: the entity's context + this user's stored messages. No AI spend.
    getThread(params, token) {
        return ApiCall('GET', `${BASE}/thread`, params, { Authorization: token || '' });
    },
    /**
     * One conversational turn, read as a Server-Sent Event stream.
     *
     * Uses `fetch` rather than ApiCall because a browser XHR buffers the whole
     * body — nothing arrives until the request finishes, which defeats the point.
     * A turn runs 45-90s and any silent connection that long gets killed by the
     * reverse proxy (a 504); the stream keeps bytes moving so it never does.
     *
     * Resolves with the same `{ user_message, message }` payload the JSON
     * endpoint used to return, so callers are unaffected.
     *
     * `onDelta` receives the reply text as the agent writes it — the first words
     * arrive around 10s in, against ~30s for the finished turn. It is display
     * only; the resolved payload is always the authoritative version.
     */
    async sendMessage(payload, token, signal, onDelta) {
        // ApiCall injects x-module-key from Redux; on a raw fetch we do it
        // ourselves, or the backend can't tell which module is asking.
        const moduleKey = store.getState()?.userData?.activeModuleKey || '';
        const res = await fetch(`${apiEndpoint}${BASE}/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: token || '',
                ...(moduleKey ? { 'x-module-key': moduleKey } : {}),
            },
            body: JSON.stringify(payload),
            // Closing the drawer aborts here, which drops the connection and lets
            // the server cancel the turn and release its per-thread lock.
            signal,
        });
        // Validation failures answer before the stream opens, as ordinary JSON.
        if (!res.headers.get('content-type')?.includes('text/event-stream')) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body?.message || `Request failed (${res.status})`);
        }
        if (!res.body)
            throw new Error('Streaming is not supported in this browser.');
        const { result, failure } = await readSse(res.body, {
            onEvent: (event, data) => {
                if (event === 'delta' && onDelta && typeof data?.text === 'string')
                    onDelta(data.text);
            },
        });
        if (failure)
            throw new Error(failure);
        // Neither terminal frame arrived — the connection dropped mid-turn.
        if (!result)
            throw new Error('The connection closed before the agent finished.');
        return result;
    },
    // The query string is inlined because ApiCall only maps a payload to params
    // on GET — on any other verb it becomes a request body, which req.query misses.
    clearThread(params, token) {
        const qs = new URLSearchParams({
            clientId: params.clientId,
            adId: params.adId,
            category: params.category,
        }).toString();
        return ApiCall('DELETE', `${BASE}/thread?${qs}`, undefined, { Authorization: token || '' });
    },
    // This user's recent AI conversations for a client (metadata only).
    getThreads(params, token) {
        return ApiCall('GET', `${BASE}/threads`, params, { Authorization: token || '' });
    },
    /**
     * The creative image the agent was shown, as a blob object-URL.
     *
     * Fetched rather than pointed at with a bare <img src>, because the endpoint
     * is auth-gated and an img tag can't send the Authorization header. Returns
     * null when the ad has no usable creative — the caller says so instead of
     * showing a broken frame. Callers must revokeObjectURL when done.
     */
    async getAdImageUrl(params, token) {
        const moduleKey = store.getState()?.userData?.activeModuleKey || '';
        const qs = new URLSearchParams({
            clientId: params.clientId,
            adId: params.adId,
            category: params.category,
        }).toString();
        try {
            const res = await fetch(`${apiEndpoint}${BASE}/ad-image?${qs}`, {
                headers: {
                    Authorization: token || '',
                    ...(moduleKey ? { 'x-module-key': moduleKey } : {}),
                },
            });
            if (!res.ok)
                return null;
            const blob = await res.blob();
            return blob.size ? URL.createObjectURL(blob) : null;
        }
        catch {
            return null;
        }
    },
};
export default aiService;
/**
 * One turn of the Report Builder assistant, streamed like sendMessage. The
 * conversation is stored in ai_chats; pass its id to continue it.
 */
export async function sendReportBuilderMessage(payload, token, signal, onDelta) {
    const moduleKey = store.getState()?.userData?.activeModuleKey || '';
    const res = await fetch(`${apiEndpoint}${BASE}/report-builder/chat`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: token || '',
            ...(moduleKey ? { 'x-module-key': moduleKey } : {}),
        },
        body: JSON.stringify(payload),
        signal,
    });
    if (!res.headers.get('content-type')?.includes('text/event-stream')) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || `Request failed (${res.status})`);
    }
    if (!res.body)
        throw new Error('Streaming is not supported in this browser.');
    const { result, failure } = await readSse(res.body, {
        onEvent: (event, data) => {
            if (event === 'delta' && onDelta && typeof data?.text === 'string')
                onDelta(data.text);
        },
    });
    if (failure)
        throw new Error(failure);
    if (!result)
        throw new Error('The connection closed before the agent finished.');
    return result;
}
// ── Report Builder conversations (ai_chats, per user, no expiry) ──────────
const RB_CONVERSATIONS = `${BASE}/report-builder/conversations`;
export const reportBuilderChatService = {
    /** One report's chats ({ report_id }), or a client's Add-page drafts ({ client_id }). */
    list(scope, token) {
        return ApiCall("GET", RB_CONVERSATIONS, scope, { Authorization: token || "" });
    },
    /** A draft joins the report it produced - once, on that report's first save. */
    link(conversationId, reportId, token) {
        return ApiCall("POST", `${RB_CONVERSATIONS}/${encodeURIComponent(conversationId)}/link`, { report_id: reportId }, { Authorization: token || "" });
    },
    get(conversationId, token) {
        return ApiCall("GET", `${RB_CONVERSATIONS}/${encodeURIComponent(conversationId)}`, "", { Authorization: token || "" });
    },
    rename(conversationId, title, token) {
        return ApiCall("PATCH", `${RB_CONVERSATIONS}/${encodeURIComponent(conversationId)}`, { title }, { Authorization: token || "" });
    },
    remove(conversationId, token) {
        return ApiCall("DELETE", `${RB_CONVERSATIONS}/${encodeURIComponent(conversationId)}`, "", { Authorization: token || "" });
    },
    markApplied(conversationId, messageId, token) {
        return ApiCall("POST", `${RB_CONVERSATIONS}/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/applied`, {}, { Authorization: token || "" });
    },
};
